/**
 * SwitchPoe — Switch > PoE.
 *
 * Per-switch power-over-ethernet view. Live per-port PoE *draw* (watts) is vendor-specific
 * and only available where the switch exposes the POWER-ETHERNET-MIB, so this is honest:
 * it shows the real per-port link state (from SNMP) and the PoE *intent* configured in Port
 * Profiles, and it says plainly when live PoE telemetry isn't available rather than faking
 * wattage. PoE enable/disable itself is managed on Port Profiles.
 */
import { formatSwitchPort } from "@/lib/switchPorts";
import { Link } from "react-router-dom";
import { useDevices, useSwitchPorts, useCatalog } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import {
  SWITCH_POLL_TIMEOUT_MS,
  normalizeLivePort,
  offlineSinceText,
  poeCardState,
  switchKnownOffline,
} from "@/lib/liveStatus";
import type { Device } from "@/types/domain";

export default function SwitchPoe() {
  // ?kind=switch is applied SERVER-SIDE. Filtering the fetched page instead searched only
  // the 100 most-recently-contacted devices, so a switch that had gone quiet — the one worth
  // opening this page for — could not appear at all.
  const { data, isLoading, error } = useDevices({ kind: "switch" });
  const profiles = useCatalog("port_profile");
  const switches = data?.items ?? [];
  const poeProfiles = (profiles.data?.items ?? []).filter((p) => p.poe_enabled !== false);

  return (
    <div>
      <PageHeader title="PoE" subtitle="Power-over-Ethernet status and budget per switch" />

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading switches…</div>}

      {/* PoE intent from port profiles — the configured policy, always available. */}
      <div className="md-card mb-4 p-3 text-sm">
        <span className="text-ink-muted">
          PoE is enabled/disabled per port via{" "}
          <Link to="../switch-portprofiles" className="text-brand hover:underline">Port Profiles</Link>.{" "}
          {poeProfiles.length > 0
            ? `${poeProfiles.length} profile(s) enable PoE.`
            : "No PoE-enabled port profiles defined yet."}
        </span>
      </div>

      {data && switches.length === 0 && (
        <EmptyState title="No switches in scope" hint="Onboard a switch to see its PoE ports here." />
      )}

      <div className="flex flex-col gap-5">
        {switches.map((sw) => <PoeCard key={sw.serial} device={sw} />)}
      </div>
    </div>
  );
}

function PoeCard({ device }: { device: Device }) {
  // OP#1860: a switch the list already reports offline is never polled (no "Polling switch…"
  // forever); every other poll is time-boxed inside useSwitchPorts. poeCardState is the one
  // decision for what the card shows -- see lib/liveStatus.
  const offline = switchKnownOffline(device);
  const q = useSwitchPorts(device.serial, { offline });
  const { data, refetch, isFetching } = q;
  const state = poeCardState(device, { data, error: q.error });
  const ports = (data?.ports ?? []).map(normalizeLivePort);
  const up = ports.filter((p) => p.status === "up").length;
  const note = "rounded border border-line bg-surface-2 p-3 text-sm text-ink-muted";

  return (
    <section className="md-card p-4" data-testid={`poe-card-${device.serial}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <span className="font-medium text-ink">{device.name}</span>
          <span className="ml-2 font-mono text-xs text-ink-muted">{device.ip ?? "—"}</span>
        </div>
        {state.kind === "ports" && <span className="text-xs text-ink-subtle">{up}/{ports.length} ports up</span>}
      </div>

      {state.kind === "offline" && (
        <div className={note} role="status">
          {offlineSinceText(state.since)} Live PoE and port data are unavailable until it reconnects.
        </div>
      )}
      {state.kind === "polling" && <div className="text-sm text-ink-muted">Polling switch…</div>}
      {state.kind === "stopped" && (
        <div className={note} role="status">
          The switch stopped responding: no answer within {Math.round(SWITCH_POLL_TIMEOUT_MS / 1000)} s.{" "}
          <button
            type="button"
            className="font-medium text-brand hover:underline disabled:opacity-50"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            {isFetching ? "Retrying…" : "Retry"}
          </button>
        </div>
      )}
      {state.kind === "error" && <ErrorBanner error={q.error} />}

      {state.kind === "unreachable" && (
        <div className={note}>
          SNMP is not reachable on this switch, so live PoE status can't be read. Enable a
          read-only SNMP community on it to see per-port power here.
        </div>
      )}

      {state.kind === "no_ports" && <div className={note}>The switch answered but reported no PoE ports.</div>}

      {state.kind === "ports" && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-ink-subtle">
                <tr className="[&>th]:py-1 [&>th]:pr-3 [&>th]:font-medium">
                  <th>Port</th><th>Link</th><th>Speed</th><th>PoE draw</th><th>Description</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {ports.map((p) => (
                  <tr key={p.key} className="border-t border-line/60 [&>td]:py-1 [&>td]:pr-3">
                    <td title={p.name}>{formatSwitchPort(p.name)}</td>
                    <td><span className={p.status === "up" ? "text-healthy" : "text-ink-subtle"}>{p.status}</span></td>
                    <td>{p.status === "up" && p.speed_mbps ? (p.speed_mbps >= 1000 ? `${p.speed_mbps / 1000}G` : `${p.speed_mbps}M`) : "—"}</td>
                    <td className="text-ink-subtle">—</td>
                    <td className="font-sans">{p.description ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 text-xs text-ink-subtle">
            Live per-port wattage requires the switch's POWER-ETHERNET-MIB and is not reported by this model over the current SNMP profile.
          </div>
        </>
      )}
    </section>
  );
}
